/**
 * ICER Health / Wantok Telehealth brand mark + lockup.
 *
 * Loads the ICER Health PNG logo from /branding/icer-logo.png. Falls
 * back to the original generated SVG "W" mark when the file isn't
 * present (so the dev environment renders fine even before the asset
 * is dropped in). Drop the logo at:
 *   web/public/branding/icer-logo.png
 * and Vite serves it at the same path. No build step needed.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'

const LOGO_SRC = '/branding/icer-logo.png'

/**
 * Brand mark.
 *
 * The ICER Health PNG is a coloured circular logo (blue/orange ring,
 * iCH wordmark inside). The platform's main header is a teal gradient,
 * so dropping the colour logo straight onto it would make it fight the
 * background. We wrap it in a soft-white round badge with a subtle
 * shadow so it pops on dark headers while still looking clean on white
 * cards (the badge is barely visible there, just adds gentle depth).
 */
export function BrandMark({ size = 44, className = '' }) {
  const [imgFailed, setImgFailed] = useState(false)
  if (imgFailed) return <FallbackSvgMark size={size} className={className} />

  // Inner image is the logo at 90% of the badge so a thin white ring
  // surrounds it — the ring is what makes the colour logo legible
  // against the dark teal app header.
  const inner = Math.round(size * 0.9)
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full bg-white shadow-md ring-1 ring-black/5 ${className}`}
      style={{ width: size, height: size }}
      aria-label="ICER Health"
      role="img"
    >
      <img
        src={LOGO_SRC}
        width={inner}
        height={inner}
        alt=""
        style={{ objectFit: 'contain', borderRadius: '50%' }}
        onError={() => setImgFailed(true)}
      />
    </span>
  )
}

// Original SVG W kept as a graceful fallback when the PNG isn't
// uploaded yet (or fails to load on a low-bandwidth network).
function FallbackSvgMark({ size = 36, className = '' }) {
  return (
    <svg
      viewBox="0 0 36 36"
      width={size}
      height={size}
      role="img"
      aria-label="Wantok"
      className={className}
    >
      <defs>
        <linearGradient id="wmark-bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"  stopColor="#2dd4bf" />
          <stop offset="55%" stopColor="#0d9488" />
          <stop offset="100%" stopColor="#115e59" />
        </linearGradient>
        <linearGradient id="wmark-shine" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.35" />
          <stop offset="50%"  stopColor="#ffffff" stopOpacity="0.05" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="36" height="36" rx="9" fill="url(#wmark-bg)" />
      <rect x="0" y="0" width="36" height="18" rx="9" fill="url(#wmark-shine)" />
      <path
        d="M8 11 L13 26 L18 16 L23 26 L28 11"
        fill="none" stroke="#fff" strokeWidth="2.6"
        strokeLinecap="round" strokeLinejoin="round"
      />
      <circle cx="28" cy="11" r="1.8" fill="#ff9a76" />
      <circle cx="28" cy="11" r="3.2" fill="#ff9a76" fillOpacity="0.25" />
    </svg>
  )
}

export function BrandLockup({ size = 48, variant = 'dark', linkTo = '/' }) {
  const wordTop    = variant === 'light' ? 'text-white' : 'text-slate-900'
  const wordBottom = variant === 'light' ? 'text-wantok-200' : 'text-wantok-700'

  const lockup = (
    <span className="flex items-center gap-3 select-none">
      {/* Slight outer glow on dark headers, softer on light. The white
          badge already gives high contrast; the shadow is just depth. */}
      <BrandMark
        size={size}
        className={
          'shrink-0 ' +
          (variant === 'light'
            ? 'drop-shadow-[0_2px_8px_rgba(0,0,0,0.18)]'
            : 'drop-shadow-[0_1px_4px_rgba(15,23,42,0.10)]')
        }
      />
      <span className="leading-[1.05]">
        <span className={`block font-display font-bold tracking-[-0.01em] text-[18px] ${wordTop}`}>
          Wantok Telehealth
        </span>
        <span className={`block text-[10px] uppercase tracking-[0.22em] font-semibold ${wordBottom}`}>
          ICER Health
        </span>
      </span>
    </span>
  )

  if (linkTo) {
    return (
      <Link to={linkTo} className="inline-flex items-center hover:opacity-95 transition-opacity">
        {lockup}
      </Link>
    )
  }
  return lockup
}
